import * as React from 'react';
import Link from 'next/link';
import { IconButton } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PageBase from '../../components/layout/PageBase';
import { HOMEBUZZ_LINK, REALREPORTS_LINK } from '../../lib/constants';
import style from '../../styles/sass/pages/Tools.module.scss';
import {
  ArrowRight,
  Medal,
  Calculator,
  AlignBottom,
  Wrench,
  House,
  Leaf,
  CalendarBlank,
  Tag,
  HandFist,
  TrendUp,
  CurrencyCircleDollar,
  CircleWavyQuestion,
  Coffee,
} from 'phosphor-react';

export interface Props {
  classes: any;
}

const styles = {
  toolsNavBtn: {
    height: 48,
    width: 48,
    borderRadius: 10,
    padding: 8,
  },
};

export function Tools(props: Props) {
  const { classes } = props;
  const ToolsList = [
    {
      bgColor: '#FFA0BE',
      description: <span>Find the most undervalued homes on the market</span>,
      icon: <Medal size={32} color="#FFFFFF" weight="regular" />,
      name: 'Best Buys',
      path: '/tools/best-buys',
    },
    {
      bgColor: '#195A50',
      description: <span>Get a breakdown of typical home closing costs</span>,
      icon: <Calculator size={32} color="#FFFFFF" weight="regular" />,
      name: 'Closing Cost Calculator',
      path: '/tools/closing-costs',
    },
    {
      bgColor: '#816646',
      description: (
        <span>Check out our selection of the best fixer-uppers available</span>
      ),
      icon: <AlignBottom size={32} color="#FFFFFF" weight="regular" />,
      name: 'Comps Chomper',
      path: '/tools/comps-chomper',
    },
    {
      bgColor: '#463264',
      description: (
        <span>Check out our selection of the best fixer-uppers available</span>
      ),
      icon: <Wrench size={32} color="#FFFFFF" weight="regular" />,
      name: 'Fixer Finder',
      path: '/tools/fixer-finder',
    },
    {
      bgColor: '#195A50',
      description: <span>See high-value investment properties</span>,
      icon: <Leaf size={32} color="#FFFFFF" weight="regular" />,
      name: 'Fresh Finds',
      path: '/tools/fresh-finds',
    },
    {
      bgColor: '#463264',
      description: (
        <span>Rate properties, leave comments, and see trending homes</span>
      ),
      icon: <House size={32} color="#FFFFFF" weight="regular" />,
      name: 'Homebuzz',
      newTab: true,
      path: HOMEBUZZ_LINK,
    },
    {
      bgColor: '#822841',
      description: <span>See monthly sales data real estate trends</span>,
      icon: <CalendarBlank size={32} color="#FFFFFF" weight="regular" />,
      name: 'Monthly Market',
      path: '/monthly-market',
    },
    {
      bgColor: '#FF7350',
      description: <span>Find out the right amount to offer on a home</span>,
      icon: <Tag size={32} color="#FFFFFF" weight="regular" />,
      name: 'Price Pointer',
      path: '/tools/price-pointer',
    },
    {
      bgColor: '#463264',
      description: <span>Maximize your rental income</span>,
      icon: <HandFist size={32} color="#FFFFFF" weight="regular" />,
      name: 'Rent Cruncher',
      path: '/tools/rent-cruncher',
    },
    {
      bgColor: '#FF7350',
      description: <span>See where property values are headed</span>,
      icon: <TrendUp size={32} color="#FFFFFF" weight="regular" />,
      name: 'Trend Spotter',
      path: '/tools/trend-spotter',
    },
    {
      bgColor: '#195A50',
      description: (
        <span>
          Get an estimate of the monthly mortgage payment you could pay,
        </span>
      ),
      icon: <CurrencyCircleDollar size={32} color="#FFFFFF" weight="regular" />,
      name: 'Mortgage Calculator',
      path: '/tools/mortgage-calculator',
    },
    // {
    //   bgColor: '#FF7350',
    //   description: <span>Get an understanding of what you can afford</span>,
    //   icon: <CircleWavyQuestion size={32} color="#FFFFFF" weight="regular" />,
    //   name: 'Home Affordability Calculator',
    //   path: '/tools/affordability-calculator',
    // },
    {
      bgColor: 'linear-gradient(140deg, #fe83db 10%, #82d3f9 75%)',
      description: <span>AI-supercharged "CARFAX" for homes</span>,
      gradient: true,
      icon: <img src="../../static/img/Tools_Icons/bhr_logo.svg" />,
      name: 'RealReports',
      newTab: true,
      path: REALREPORTS_LINK,
    },
  ];

  const renderToolList = () => {
    return ToolsList.map((tool, index) => (
      <React.Fragment key={index}>
        <Link href={tool.path}>
          <a
            target={tool.newTab ? '_blank' : '_self'}
            rel={tool.newTab ? 'noopener noreferrer' : ''}
            className={style.tooItem}
          >
            <div className={style.toolsList} key={index}>
              <div>
                <div className={`${style.iconContainer}`}>
                  {/* <img src="../../static/img/Tools_Icons/ccc.svg" /> */}
                  <IconButton
                    type="button"
                    className={style.icon}
                    style={
                      tool.gradient
                        ? { background: tool.bgColor }
                        : { backgroundColor: tool.bgColor }
                    }
                  >
                    {tool.icon}
                  </IconButton>
                </div>
                <h2 className={`${style.toolTitle}`}>{tool.name}</h2>
                <p className={style.toolSubText}>{tool.description}</p>
              </div>
              <div style={{ alignSelf: 'flex-end' }}>
                <ArrowRight color="#2C5853" size={25} />
              </div>
            </div>
          </a>
        </Link>
      </React.Fragment>
    ));
  };
  return (
    <PageBase
      title="Torii Real Estate - Massachusetts real estate search tools"
      disableOverflow
    >
      <div className={`page-container ${style.toolsPage} row`}>
        {/* <div className={style.toolsBlob} /> */}

        <section className={`col-xs-12 ${style.toolsHeroBanner}`}>
          <div className={style.toolsContainerWrap}>
            <div className={style.toolsTextContainer}>
              <h1 className={style.heroHeadingText}>Homebuying Tools</h1>
              <span className={style.heroDesc}>
                Where are property values in my neighborhood headed? What would
                be my monthly mortgage payment? We’ve built you a suite of
                helpful homebuying tools to answer these questions and much
                more.
              </span>
            </div>
            <div className={style.heroImageWrap}>
              <img
                src={'/static/img/tools/header_blobs.svg'}
                className={style.heroImage}
                alt="buy-hero-banner-one"
              />
            </div>
          </div>
        </section>

        <div className={style.toolContainerWrap}>
          <div className={`col-xs-12 ${style.toolsListContainer}`}>
            {renderToolList()}
          </div>

          <section className={`col-xs-12 ${style.blog_section}`}>
            <h2 className={style.blog_section_title}>Read more on our blog</h2>

            <div className={style.blog_cards_wrap}>
              <div className={style.blog_card}>
                <a
                  href="https://blog.toriihomes.com/yes-owning-is-still-better-than-renting-heres-why/"
                  target="_blank"
                >
                  <img src="/static/img/tools/blogImg_01.png" />
                  <h3 className={style.blog_title}>
                    Yes, Owning is Still Better Than Renting. Here’s Why.
                  </h3>
                </a>
              </div>

              <div className={style.blog_card}>
                <a
                  href="https://blog.toriihomes.com/introducing-torii-cash-offers-for-homebuyers/"
                  target="_blank"
                >
                  <img src="/static/img/tools/blogImg_02.png" />
                  <h3 className={style.blog_title}>
                    Introducing: Torii Cash Offers for the Competitive Home
                    Buyer
                  </h3>
                </a>
              </div>

              <div className={style.blog_card}>
                <a
                  href="https://blog.toriihomes.com/buying-a-fixer-upper-first-time-buyer-pros-and-cons/"
                  target="_blank"
                >
                  <img src="/static/img/tools/blogImg_03.png" />
                  <h3 className={style.blog_title}>
                    Buying a Fixer-Upper as a First-Time Home Buyer
                  </h3>
                </a>
              </div>
            </div>
          </section>
        </div>
      </div>
    </PageBase>
  );
}

export default withStyles(styles)(Tools);
